/**
 * 출하 검사.
 *
 * **채우는 화면과 인쇄물을 나눈다.** 채우기는 콘솔의 다른 화면과 같은 일반 웹 화면에서 하고,
 * [성적서 보기] 를 누르면 같은 내용을 A4 양식(`InspectionReport.tsx`)으로 옮겨 그려 인쇄한다.
 *
 * 한때 A4 한 장을 그대로 그려 놓고 그 위에서 채웠다 — 종이에 어떻게 나오는지 바로 보이는 것은
 * 좋았지만, 210mm 폭의 작은 글자·좁은 칸으로 반나절 검사를 하기가 고역이었고 휴대폰·태블릿을
 * 들고 현장을 돌 때는 거의 쓸 수 없었다. 종이에 어떻게 나오는지는 성적서 보기에서 확인한다.
 *
 * 성적서 보기는 주소(`?view=report`)로 남긴다. 뒤로 가기가 편집 화면으로 돌아와야 하고,
 * 성적서를 연 채로 새로고침해도 그 자리에 있어야 한다.
 *
 * 검사표는 서버에 있다(`inspection/draft.ts`). 시스템 계정들이 같은 표를 보고 이어서 채우며,
 * 고친 것은 저절로 저장된다. 서명받는 정본은 여전히 성적서 보기에서 인쇄한 종이다.
 *
 * 항목마다 **누가 판정하는가**(자동·사람·현장)가 붙는다. 위의 [검사 방법] 에서 자동만 걸러 앉은
 * 자리에서 끝내고, 남은 사람 항목만 들고 현장에 가면 방문 한 번이 짧아진다. 거르는 것은 보이는
 * 줄뿐이라 판정과 묶음의 판정 수는 전체 기준 그대로다 — 성적서에는 이 구분도 거르기도 실리지 않는다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Printer, RefreshCw, RotateCcw } from 'lucide-react';
import type { InspectionRecord, Me, SignageOneClient } from '../api/client.js';
import { useAsync } from '../hooks.js';
import { formatSeconds, Panel } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { DateField } from '../components/DateField.js';
import {
  ALL_ITEMS,
  INSPECTION_SECTIONS,
  SUMMARY_LABEL,
  VERDICT_LABEL,
  VERIFY_LABEL,
  WHERE_LABEL,
  pendingByVerify,
  type PendingByVerify,
  itemNumber,
  summarize,
  type InspectionItem,
  type Summary,
  type Verdict,
} from '../inspection/checklist.js';
import { useInspectionDraft, type SaveStatus } from '../inspection/draft.js';
import { emptyState, todayISO, type ReportState } from '../inspection/report.js';
import { savedByLabel } from '../inspection/sync.js';
import {
  fleetLabel,
  issuersLabel,
  printedAt,
  readyLabel,
  resolutionOf,
  stateOf,
  versionSpread,
  type SystemSnapshot,
} from '../inspection/snapshot.js';
import { InspectionReport, originLabel } from './InspectionReport.js';
import { confirmDialog } from '../components/ConfirmDialog.js';

export function InspectionScreen({ client, me }: { client: SignageOneClient; me: Me | null }): ReactNode {
  const today = useMemo(() => todayISO(), []);
  const draft = useInspectionDraft(client, today);
  const { state, update } = draft;
  const [params, setParams] = useSearchParams();
  const viewingReport = params.get('view') === 'report';

  /*
   * 성적서를 열면 첫 줄부터 보인다. 본문은 창이 아니라 셸의 `main` 이 스크롤하므로 그것을
   * 올린다 — 그대로 두면 긴 검사표를 내려온 위치에서 성적서 중간이 뜬다.
   */
  const reportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (viewingReport) reportRef.current?.closest('main')?.scrollTo?.(0, 0);
  }, [viewingReport]);

  const ready = useAsync(() => client.ready(), [client]);
  const metrics = useAsync(() => client.metrics(), [client]);
  const fleet = useAsync(() => client.fleetSummary(), [client]);
  /*
   * 숨긴 화면까지 받는다. 목록에서 치워 둔 기기도 **납품 대수에는 들어간다** — 실물을
   * 대조하는 표에서 한 대가 비면 검사자는 없는 기기를 찾아 창고를 뒤진다.
   */
  const displays = useAsync(() => client.allDisplays({ hidden: 'include' }), [client]);

  /**
   * 무엇만 보고 있나 — 전체 · 자동 · 사람.
   *
   * **거르는 것은 보이는 줄뿐이다.** 판정도, 묶음의 판정 수도 거르기와 상관없이 전체 기준이다 —
   * 걸러 놓은 채로 "이 묶음 다 봤다" 고 읽으면 안 본 항목이 합격으로 인쇄된다.
   */
  const [only, setOnly] = useState<'all' | 'auto' | 'manual'>('all');

  const summary = useMemo(() => summarize(state?.results ?? {}), [state?.results]);
  const pending = useMemo(() => pendingByVerify(state?.results ?? {}), [state?.results]);
  const snapshot: SystemSnapshot = {
    ready: ready.data,
    readyError: ready.error,
    metrics: metrics.data,
    fleet: fleet.data,
    displays: displays.data?.items ?? [],
    displaysLoaded: displays.data !== null,
    displaysError: displays.error,
    account: me?.account?.email ?? me?.id ?? '—',
  };

  const setHeader = useCallback(
    (key: keyof ReportState['header'], value: string): void => {
      update((prev) => ({ ...prev, header: { ...prev.header, [key]: value } }));
    },
    [update],
  );

  const setVerdict = useCallback(
    (id: string, verdict: Verdict | undefined): void => {
      update((prev) => {
        const results = { ...prev.results };
        if (verdict) results[id] = verdict;
        else delete results[id];
        return { ...prev, results };
      });
    },
    [update],
  );

  const setNote = useCallback(
    (id: string, note: string): void => {
      update((prev) => ({ ...prev, notes: { ...prev.notes, [id]: note } }));
    },
    [update],
  );

  const reset = useCallback(async (): Promise<void> => {
    /*
     * 되돌릴 수 없는 일이라 한 번 묻는다. 검사표는 반나절 걸려 채우는 문서고, 잘못
     * 눌러 날리면 현장에서 처음부터 다시 돈다.
     */
    const ok = await confirmDialog({
      title: '판정과 비고를 모두 지울까요?',
      body: '서버의 검사표도 지워져 다른 사용자에게도 빈 표로 보입니다.',
      confirmLabel: '모두 지우기',
      danger: true,
    });
    if (!ok) return;
    update(() => emptyState(today));
  }, [today, update]);

  const openReport = (): void => setParams({ view: 'report' });

  // 불러오기 전에는 표를 그리지 않는다 — 빈 표를 먼저 그리면 판정이 없는 줄 알고 다시 찍는다.
  if (!state) {
    return (
      <div className="screen">
        <div className="ins-bar no-print">
          <div className="ins-progress">
            <SaveIndicator status={draft.status} onRetry={draft.retry} />
          </div>
        </div>
      </div>
    );
  }

  if (viewingReport) {
    return (
      <div ref={reportRef} className="screen">
        <div className="ins-bar no-print">
          <div className="ins-progress">
            {/* 뒤로 가기와 같은 곳이다 — 편집 화면에서 성적서를 열었으니 기록을 되짚는다. */}
            <button type="button" className="btn" onClick={() => setParams({})}>
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> 편집으로 돌아가기
            </button>
            <SummaryBadges summary={summary} pending={pending} />
          </div>
          <div className="ins-actions">
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              <Printer className="h-3.5 w-3.5" aria-hidden="true" /> 인쇄 · PDF 저장
            </button>
          </div>
        </div>
        <p className="hint no-print ins-help">
          인쇄 창에서 용지 <b>A4</b>, 배율 <b>기본(100%)</b>, <b>배경 그래픽</b> 켜기를 확인하세요.
          <HelpTip text="배경 그래픽을 끄면 판정 칸의 채움이 인쇄되지 않습니다." label="인쇄 설정 도움말" />
          {summary.verdict === 'pending'
            ? ` 미판정 항목 ${summary.pending}개가 있어 종합 판정이 ‘검사 중’으로 인쇄됩니다.`
            : ''}
        </p>
        <InspectionReport state={state} summary={summary} snapshot={snapshot} />
      </div>
    );
  }

  return (
    <div className="screen">
      {/*
        채우는 화면을 그대로 인쇄하면 입력칸·단추가 찍힌 웹 페이지가 나온다. 그것을 성적서로
        착각해 서명받지 않게, 인쇄물에는 안내 한 줄만 남기고 나머지는 뺀다.
      */}
      <p className="print-only">
        출하 검사 성적서는 [성적서 보기] 화면에서 인쇄하세요. 이 화면은 검사표 작성용입니다.
      </p>
      <div className="ins-bar no-print">
        <div className="ins-progress">
          <SummaryBadges summary={summary} pending={pending} />
          <SaveIndicator status={draft.status} onRetry={draft.retry} />
          <HelpTip
            text="판정과 비고는 자동 저장되어 이 서버의 시스템 계정이 함께 봅니다. 서명용 완성본은 성적서 보기에서 인쇄하거나 PDF로 보관하세요."
            label="출하 검사 도움말"
          />
        </div>
        <div className="ins-actions">
          <button type="button" className="btn" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> 초기화
          </button>
          <button type="button" className="btn btn-primary" onClick={openReport}>
            <FileText className="h-3.5 w-3.5" aria-hidden="true" /> 성적서 보기
          </button>
        </div>
      </div>
      {draft.status.kind === 'conflict' ? (
        <ConflictNotice current={draft.status.current} onTakeServer={draft.takeServer} onKeepMine={draft.keepMine} />
      ) : null}

      <div className="screen no-print">
        <VerifyFilterPanel only={only} onChange={setOnly} results={state.results} />

        <Panel title="납품 정보" help="성적서 머리에 인쇄됩니다. 비워 둔 칸은 빈칸으로 인쇄되어 손으로 채울 수 있습니다.">
          <div className="ins-form">
            <TextField label="문서번호" value={state.header.docNo} onChange={(value) => setHeader('docNo', value)} />
            <TextField label="고객사" value={state.header.customer} onChange={(value) => setHeader('customer', value)} />
            <TextField label="설치 현장" value={state.header.site} onChange={(value) => setHeader('site', value)} />
            <TextField
              label="납품 구성"
              value={state.header.system}
              onChange={(value) => setHeader('system', value)}
              placeholder="예) SignageOne 서버 1식 · 화면 12대"
            />
            <TextField
              label="검사일"
              type="date"
              value={state.header.inspectedOn}
              onChange={(value) => setHeader('inspectedOn', value)}
            />
            <TextField label="검사자" value={state.header.inspector} onChange={(value) => setHeader('inspector', value)} />
            <TextField label="고객 입회자" value={state.header.witness} onChange={(value) => setHeader('witness', value)} />
          </div>
        </Panel>

        <Panel title="검사 시점 시스템 현황" help="서버가 현재 보고하는 값입니다. 성적서에는 출력 시점의 값이 인쇄됩니다.">
          <dl className="kv ins-kv">
            <dt>콘솔 주소</dt>
            <dd className="mono">{originLabel()}</dd>
            <dt>서버 상태</dt>
            <dd>{readyLabel(snapshot.ready, snapshot.readyError)}</dd>
            <dt>서버 가동 시간</dt>
            <dd>{snapshot.metrics ? formatSeconds(snapshot.metrics.uptime_sec) : '—'}</dd>
            <dt>신원 발급자</dt>
            <dd className={snapshot.ready?.checks.identity_issuers.length ? 'mono' : undefined}>{issuersLabel(snapshot.ready)}</dd>
            <dt>등록 화면</dt>
            <dd>{fleetLabel(snapshot.fleet)}</dd>
            <dt>배정 없음</dt>
            <dd>{snapshot.fleet ? `${snapshot.fleet.unassigned}대` : '—'}</dd>
            <dt>에이전트 버전</dt>
            <dd>{snapshot.displaysLoaded ? versionSpread(snapshot.displays) : '—'}</dd>
            <dt>검사 수행 계정</dt>
            <dd className="mono">{snapshot.account}</dd>
          </dl>
        </Panel>

        {INSPECTION_SECTIONS.map((section, sectionIndex) => {
          const judged = section.items.filter((item) => state.results[item.id]).length;
          const failed = section.items.filter((item) => state.results[item.id] === 'fail').length;
          const shown = only === 'all' ? section.items : section.items.filter((item) => item.verify === only);
          /* 거른 결과가 한 줄도 없으면 묶음째 감춘다 — 빈 상자만 늘어서면 훑기가 더 어렵다. */
          if (shown.length === 0) return null;
          return (
            <Panel
              key={section.id}
              title={`${sectionIndex + 1}. ${section.title}`}
              subtitle={section.intro}
              actions={
                <span
                  className={`badge ${failed > 0 ? 'badge-danger' : judged === section.items.length ? 'badge-ok' : 'badge-neutral'}`}
                >
                  {judged}/{section.items.length} 판정{failed > 0 ? ` · 불합격 ${failed}` : ''}
                  {only === 'all' ? '' : ` · ${VERIFY_LABEL[only]} ${shown.length}줄`}
                </span>
              }
            >
              <ol className="ins-items">
                {section.items.map((item, itemIndex) => {
                  const verdict = state.results[item.id];
                  const note = state.notes[item.id] ?? '';
                  /* 불합격의 근거는 비고에 남아야 한다 — 비어 있으면 칸이 그것을 요구한다. */
                  const needsReason = verdict === 'fail' && note.trim() === '';
                  /* 거르기는 줄만 감춘다. 번호는 **전체 기준 순번**이라 걸러도 같은 항목을 가리킨다. */
                  if (only !== 'all' && item.verify !== only) return null;
                  return (
                    <li key={item.id} className={`ins-item ${verdict ? '' : 'is-pending'}`}>
                      <span className="ins-item-no">{itemNumber(sectionIndex, itemIndex)}</span>
                      <div className="ins-item-body">
                        <p className="ins-item-title">
                          {item.title}
                          {item.critical ? <span className="badge badge-danger">필수</span> : null}
                          <VerifyBadge item={item} />
                        </p>
                        <p className="hint">{item.how}</p>
                        <p className="ins-item-criteria">
                          <span>합격 기준</span> {item.criteria}
                        </p>
                      </div>
                      <VerdictButtons
                        value={verdict}
                        label={item.title}
                        onChange={(next) => setVerdict(item.id, next)}
                      />
                      <input
                        className="input ins-item-note"
                        value={note}
                        placeholder={needsReason ? '불합격 사유를 입력하세요' : '비고: 측정값·예외 사유'}
                        aria-label={`${item.title} 비고`}
                        aria-invalid={needsReason || undefined}
                        onChange={(event) => setNote(item.id, event.target.value)}
                      />
                    </li>
                  );
                })}
              </ol>
            </Panel>
          );
        })}

        <Panel
          title={`${INSPECTION_SECTIONS.length + 1}. 납품 기기 목록`}
          help="콘솔에 등록된 화면입니다. 기기 일련번호는 인쇄한 성적서에 실물과 대조해 손으로 적습니다."
        >
          {snapshot.displays.length === 0 ? (
            <p className="empty">
              {snapshot.displaysLoaded || snapshot.displaysError
                ? (snapshot.displaysError ?? '등록된 화면이 없습니다. 기기를 등록한 뒤 다시 확인하세요.')
                : '불러오는 중…'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table ins-devices">
                <thead>
                  <tr>
                    <th className="col-num">번호</th>
                    <th>화면 이름</th>
                    <th>설치 위치</th>
                    <th>해상도 · 방향</th>
                    <th>에이전트</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.displays.map((display, index) => (
                    <tr key={display.id}>
                      <td className="col-num cell-muted">{index + 1}</td>
                      <td className="cell-strong">{display.name}</td>
                      <td>{display.location || '—'}</td>
                      <td>
                        {resolutionOf(display) || '—'} · {display.orientation === 'portrait' ? '세로' : '가로'}
                        {display.rotation ? ` ${display.rotation}°` : ''}
                      </td>
                      <td className="mono">{display.status.app_version ?? '미보고'}</td>
                      <td>{stateOf(display)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title={`${INSPECTION_SECTIONS.length + 2}. 종합 소견`} subtitle="불합격 항목의 조치 계획과 잔여 과제">
          <div className="p-4">
            <textarea
              className="input ins-conclusion"
              rows={5}
              value={state.conclusion}
              aria-label="종합 소견"
              onChange={(event) => update((prev) => ({ ...prev, conclusion: event.target.value }))}
            />
          </div>
        </Panel>

        {/* 긴 검사표를 끝까지 내려온 자리에서 바로 성적서로 넘어간다. */}
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={openReport}>
            <FileText className="h-3.5 w-3.5" aria-hidden="true" /> 성적서 보기
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 이 항목을 누가 판정하는가 — 줄마다 한눈에.
 *
 * '자동' 은 "사람이 안 봐도 된다" 가 아니라 **도구가 같은 답을 낼 수 있다**는 뜻이다. 검사자가
 * 직접 눌러 확인해도 결과는 같다. '사람' 은 눈·귀·다른 망·실물에 판정이 걸려 있어 도구가 흉내
 * 내면 그 합격이 거짓이 되는 것이고, 그중 기기 앞에 서야 하는 것에 '현장' 이 하나 더 붙는다.
 */
function VerifyBadge({ item }: { item: InspectionItem }): ReactNode {
  return (
    <>
      <span
        className={`badge ${item.verify === 'auto' ? 'badge-ok' : 'badge-warn'}`}
        title={
          item.verify === 'auto'
            ? 'API 응답·설정 값·DB 상태로 판정합니다. 도구로 확인할 수 있습니다.'
            : '눈·귀·다른 망·실물로 판정합니다. 사람이 확인해야 합니다.'
        }
      >
        {VERIFY_LABEL[item.verify]}
      </span>
      {item.where ? (
        <span className="badge badge-info" title="기기 앞에서 확인해야 하는 항목입니다. 현장 방문이 필요합니다.">
          {WHERE_LABEL[item.where]}
        </span>
      ) : null}
    </>
  );
}

/**
 * 검사 방법으로 갈라 보기.
 *
 * 검사표를 위에서 아래로 훑으면 "지금 앉아서 할 수 있는 것" 과 "기기 앞에 서야 하는 것" 이 섞여
 * 나온다. 9/18 검수가 미판정 26건으로 끝났고 그중 24건이 사람·기기가 있어야 하는 것이었다.
 * 먼저 자동만 걸러 끝내고 남은 사람 항목만 들고 현장에 가면 방문 한 번이 짧아진다.
 *
 * **거르는 것은 보이는 줄뿐이다** — 판정도 묶음의 판정 수도 전체 기준 그대로다.
 */
function VerifyFilterPanel({
  only,
  onChange,
  results,
}: {
  only: 'all' | 'auto' | 'manual';
  onChange: (next: 'all' | 'auto' | 'manual') => void;
  results: ReportState['results'];
}): ReactNode {
  const pending = pendingByVerify(results);
  const total = {
    all: ALL_ITEMS.length,
    auto: ALL_ITEMS.filter((item) => item.verify === 'auto').length,
    manual: ALL_ITEMS.filter((item) => item.verify === 'manual').length,
  };
  const left = {
    all: pending.auto + pending.manual + pending.field,
    auto: pending.auto,
    manual: pending.manual + pending.field,
  };
  const choices = [
    { key: 'all' as const, label: '전체' },
    { key: 'auto' as const, label: VERIFY_LABEL.auto },
    { key: 'manual' as const, label: VERIFY_LABEL.manual },
  ];

  return (
    <Panel
      title="검사 방법"
      help="거르면 보이는 줄만 줄고, 판정과 묶음별 판정 수는 전체 기준입니다. 애매한 항목은 사람으로 분류했습니다. 성적서에는 인쇄되지 않습니다."
      actions={
        <span className="badge badge-neutral">
          자동 {total.auto} · 사람 {total.manual}
        </span>
      }
    >
      <div className="space-y-2 p-4">
        <div className="flex flex-wrap gap-1.5">
          {choices.map((choice) => (
            <button
              key={choice.key}
              type="button"
              className={`btn btn-tiny ${only === choice.key ? 'btn-primary' : ''}`}
              aria-pressed={only === choice.key}
              onClick={() => onChange(choice.key)}
            >
              {choice.label} {total[choice.key]}
              <span className="cell-muted">· 남은 {left[choice.key]}</span>
            </button>
          ))}
        </div>
        <p className="hint">
          <b>자동</b> API 응답·설정 값·DB 상태로 판정 · <b>사람</b> 눈·귀·다른 망·실물로 판정 · 그중 <b>현장</b>{' '}
          {ALL_ITEMS.filter((item) => item.where === 'field').length}개(남은 {pending.field}건)는 기기 앞에서 확인
        </p>
      </div>
    </Panel>
  );
}

/**
 * 저장 상태 한 줄. 서버에 올라갔는지를 검사자가 늘 볼 수 있어야 한다 — 저장이 조용히 실패한
 * 채 반나절을 채우면 창을 닫는 순간 전부 사라진다.
 */
function SaveIndicator({ status, onRetry }: { status: SaveStatus; onRetry: () => void }): ReactNode {
  switch (status.kind) {
    case 'loading':
      return <span className="hint">검사표를 불러오는 중…</span>;
    case 'load-failed':
      return (
        <span className="ins-save is-error">
          검사표를 불러오지 못했습니다 — {status.message}
          <button type="button" className="btn" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> 다시 불러오기
          </button>
        </span>
      );
    case 'pending':
    case 'saving':
      return <span className="hint">저장 중…</span>;
    case 'failed':
      return (
        <span className="ins-save is-error">
          저장하지 못했습니다 — {status.message}
          <button type="button" className="btn" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> 다시 저장
          </button>
        </span>
      );
    case 'conflict':
      return <span className="ins-save is-error">다른 사용자가 먼저 수정했습니다. 아래에서 선택하세요.</span>;
    case 'saved':
      return (
        <span className="hint">
          {status.at ? `저장됨 · ${printedAt(new Date(status.at))} · ${savedByLabel(status.by)}` : '저장 기록 없음'}
        </span>
      );
  }
}

/**
 * 읽은 뒤 누가 먼저 고쳤을 때. 어느 쪽을 남길지는 사람이 고른다 — 서버가 합치면 두 사람이
 * 같은 항목에 다른 판정을 찍었을 때 무엇이 남았는지 아무도 모른다.
 */
function ConflictNotice({
  current,
  onTakeServer,
  onKeepMine,
}: {
  current: InspectionRecord;
  onTakeServer: () => void;
  onKeepMine: () => void;
}): ReactNode {
  const when = current.updated_at ? printedAt(new Date(current.updated_at)) : '방금';
  return (
    <div className="ins-conflict no-print" role="alert">
      <p>
        <b>{savedByLabel(current.updated_by)}</b> 님이 검사표를 먼저 수정했습니다({when}). 내 변경은 아직 저장되지
        않았으며, 서버 내용을 불러오면 버려집니다.
      </p>
      <div className="ins-actions">
        <button type="button" className="btn" onClick={onTakeServer}>
          서버 내용 불러오기
        </button>
        <button type="button" className="btn btn-danger" onClick={onKeepMine}>
          내 내용으로 덮어쓰기
        </button>
      </div>
    </div>
  );
}

function SummaryBadges({ summary, pending }: { summary: Summary; pending: PendingByVerify }): ReactNode {
  return (
    <>
      <span
        className={`badge ${summary.verdict === 'fail' ? 'badge-danger' : summary.verdict === 'pass' ? 'badge-ok' : 'badge-warn'}`}
      >
        {SUMMARY_LABEL[summary.verdict]}
      </span>
      <span className="hint">
        {summary.judged}/{summary.total} 판정 · 합격 {summary.pass} · 불합격 {summary.fail} · 해당 없음 {summary.na} ·
        미판정 {summary.pending}
      </span>
      {summary.criticalFail > 0 ? <span className="badge badge-danger">필수 항목 불합격 {summary.criticalFail}</span> : null}
      {summary.pending > 0 ? (
        /*
         * 남은 것을 **누가 판정하느냐로** 보여준다. 검사가 며칠에 걸쳐 끊기는 이유는 늘 같다 —
         * 기기 앞에 서야 하는 항목을 뒤늦게 발견해 날을 다시 잡는다. 도구로 돌릴 것, 자리에서
         * 사람이 볼 것, 방문 날을 잡을 것을 먼저 갈라 준다.
         */
        <span className="hint">
          남은 {summary.pending}: 자동 {pending.auto} · 사람 {pending.manual + pending.field}
          {pending.field > 0 ? ` (그중 현장 ${pending.field})` : ''}
        </span>
      ) : null}
    </>
  );
}

/**
 * 판정 단추 — 합격 · 불합격 · 해당 없음 중 하나.
 *
 * 같은 값을 다시 누르면 미판정으로 돌아간다. 잘못 누른 판정을 지울 방법이 없으면
 * 검사자는 틀린 채로 인쇄하거나 전체를 초기화한다.
 */
function VerdictButtons({
  value,
  label,
  onChange,
}: {
  value: Verdict | undefined;
  label: string;
  onChange: (verdict: Verdict | undefined) => void;
}): ReactNode {
  return (
    <div className="ins-verdicts" role="group" aria-label={`${label} 판정`}>
      {(['pass', 'fail', 'na'] as const).map((verdict) => (
        <button
          key={verdict}
          type="button"
          aria-pressed={value === verdict}
          className={`ins-verdict ins-verdict-${verdict} ${value === verdict ? 'is-on' : ''}`}
          onClick={() => onChange(value === verdict ? undefined : verdict)}
        >
          {VERDICT_LABEL[verdict]}
        </button>
      ))}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'date';
}): ReactNode {
  return (
    <label className="field">
      <span>{label}</span>
      {type === 'date' ? (
        <DateField value={value} onChange={onChange} label={label} clearable />
      ) : (
        <input
          className="input"
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  );
}
